import type {
    JobItem,
    JobTypeInfoItem,
} from "@mrflark/core/hooks";
import {
    useJobsStream,
} from "@mrflark/core/hooks";
import {
    formatJobType,
} from "@mrflark/core/utils";
import type {
    ComponentChildren,
    JSX,
} from "preact";
import {
    useState,
} from "preact/hooks";
import {
    Alert,
} from "../mod.tsx";
import {
    DispatcherSettingsCard,
} from "./DispatcherSettingsCard.tsx";
import {
    JobDetailModal,
} from "./JobDetailModal.tsx";
import {
    JobHistoryTable,
} from "./JobHistoryTable.tsx";
import {
    JobSummaryCards,
} from "./JobSummaryCards.tsx";
import {
    JobTypeCards,
} from "./JobTypeCards.tsx";
import {
    RunJobModal,
} from "./RunJobModal.tsx";
import {
    WorkersCard,
} from "./WorkersCard.tsx";
import type {
    SettingConfig,
} from "./types.ts";

type RunJobTarget = {
    jobType: string;
    label: string;
    typeInfo: JobTypeInfoItem;
};

// passed into headerSlot when it's a function so the slot can trigger a
// refresh of the underlying jobs stream — e.g. after an admin "Clear queue"
// action where the consumer wants the table to re-fetch immediately
// instead of waiting for the poll/SSE tick.
export type AdminJobsHeaderHelpers = {
    refresh: () => void;
    refreshing: boolean;
};

export type AdminJobsPageProps = {
    baseUrl: string;
    jobTypeLabels?: Record<string, string>;
    jobTypes?: string[];
    settingConfigs?: SettingConfig[];
    sidebarLayout?: boolean;
    extraSummaryCards?: ComponentChildren;
    extraDetailActions?: (job: JobItem) => ComponentChildren;
    headerSlot?:
        | ComponentChildren
        | ((helpers: AdminJobsHeaderHelpers) => ComponentChildren);
    statusOptions?: string[];
    maxWorkerSlots?: number;
    enableStream?: boolean;
    jobPollIntervalMs?: number;
    defaultRangeMinutes?: number;
    pageSize?: number;
};

export const AdminJobsPage = ({
    baseUrl,
    jobTypeLabels,
    jobTypes: jobTypesProp,
    settingConfigs,
    sidebarLayout: sidebarLayoutProp,
    extraSummaryCards,
    extraDetailActions,
    headerSlot,
    statusOptions,
    maxWorkerSlots,
    enableStream,
    jobPollIntervalMs,
    defaultRangeMinutes = 1440,
    pageSize: initialPageSize,
}: AdminJobsPageProps): JSX.Element => {
    const stream = useJobsStream({
        baseUrl,
        enableStream,
        jobPollIntervalMs,
        pageSize: initialPageSize,
        timePeriod: defaultRangeMinutes,
    });

    const [runJobTarget, setRunJobTarget] = useState<RunJobTarget | null>(null);

    const resolvedLabels: Record<string, string> = Object.fromEntries(
        stream.types.map((typeInfo) => [
            typeInfo.jobType,
            typeInfo.label ?? jobTypeLabels?.[typeInfo.jobType] ??
                formatJobType(typeInfo.jobType),
        ]),
    );

    const resolvedJobTypes = jobTypesProp ??
        (jobTypeLabels
            ? Object.keys(jobTypeLabels)
            : stream.types.map((typeInfo) => typeInfo.jobType));

    const resolvedJobTypeSet = new Set(resolvedJobTypes);
    const filteredTypes = stream.types.filter((typeInfo) =>
        resolvedJobTypeSet.has(typeInfo.jobType)
    );

    const handleJobClick = (job: JobItem): void => {
        stream.selectJob(job.id);
    };

    const handleRequestRunWithPayload = (typeInfo: JobTypeInfoItem): void => {
        setRunJobTarget({
            jobType: typeInfo.jobType,
            label: typeInfo.label ?? resolvedLabels[typeInfo.jobType] ??
                formatJobType(typeInfo.jobType),
            typeInfo,
        });
    };

    const handleRunJobConfirm = (
        jobType: string,
        payload: Record<string, unknown>,
    ): void => {
        stream.runJob(jobType, payload);
    };

    const hasSettings = settingConfigs && settingConfigs.length > 0;
    const useSidebarLayout = sidebarLayoutProp ?? hasSettings;

    const jobHistoryTable = (
        <JobHistoryTable
            jobs={stream.jobs}
            totalJobs={stream.totalJobs}
            page={stream.page}
            pageSize={stream.pageSize}
            typeFilters={stream.typeFilters}
            statusFilters={stream.statusFilters}
            timePeriod={stream.timePeriod}
            startTime={stream.startTime}
            endTime={stream.endTime}
            streamIntervalMs={stream.streamIntervalMs}
            jobTypes={resolvedJobTypes}
            jobTypeLabels={resolvedLabels}
            statusOptions={statusOptions}
            onPageChange={stream.setPage}
            onPageSizeChange={stream.setPageSize}
            onTypeFiltersChange={stream.setTypeFilters}
            onStatusFiltersChange={stream.setStatusFilters}
            onTimePeriodChange={stream.setTimePeriod}
            onStartTimeChange={stream.setStartTime}
            onEndTimeChange={stream.setEndTime}
            onStreamIntervalChange={stream.setStreamIntervalMs}
            sort={stream.sort}
            onSortChange={stream.setSort}
            onJobClick={handleJobClick}
            headerSlot={typeof headerSlot === "function"
                ? headerSlot({
                    refresh: stream.refresh,
                    refreshing: stream.refreshing,
                })
                : headerSlot}
            onRefresh={stream.refresh}
            refreshing={stream.refreshing}
            onBulkCancel={stream.bulkCancel}
            onBulkRetry={stream.bulkRetry}
            onBulkDelete={stream.bulkDelete}
            onBulkReschedule={stream.bulkReschedule}
        />
    );

    const workersCard = (
        <WorkersCard
            workers={stream.workers}
            dispatcherRunning={stream.dispatcherRunning}
            sessionRanges={stream.sessionRanges}
            jobTypeLabels={resolvedLabels}
            maxSlots={maxWorkerSlots}
            onWorkerClick={(jobId) => stream.selectJob(jobId)}
            compact={useSidebarLayout}
        />
    );

    const errorAlert = stream.error && (
        <Alert variant="error">
            {stream.error}
        </Alert>
    );

    const jobDetailModal = (
        <JobDetailModal
            isOpen={stream.selectedJob !== null}
            job={stream.selectedJob}
            logs={stream.selectedJobLogs}
            loading={stream.detailLoading}
            actionLoading={stream.actionLoading}
            jobTypeLabels={resolvedLabels}
            onClose={() => stream.selectJob(null)}
            onRetry={(jobId) => stream.retryJob(jobId)}
            onCancel={(jobId) => stream.cancelJob(jobId)}
            extraActions={stream.selectedJob && extraDetailActions
                ? extraDetailActions(stream.selectedJob)
                : undefined}
        />
    );

    const runJobModal = runJobTarget && (
        <RunJobModal
            isOpen
            jobType={runJobTarget.jobType}
            jobTypeLabel={runJobTarget.label}
            payloadFields={runJobTarget.typeInfo.payloadFields ?? []}
            onConfirm={handleRunJobConfirm}
            onClose={() => setRunJobTarget(null)}
        />
    );

    const invocableTypes = filteredTypes.filter((typeInfo) =>
        typeInfo.manuallyInvocable !== false
    );

    const jobTypeCards = (vertical: boolean): JSX.Element => (
        <JobTypeCards
            jobTypes={vertical ? invocableTypes : filteredTypes}
            jobTypeLabels={resolvedLabels}
            onRunNow={(jobType) => stream.runJob(jobType)}
            onRequestRunWithPayload={handleRequestRunWithPayload}
            vertical={vertical}
        />
    );

    if (useSidebarLayout) {
        return (
            <>
                <div className="grid grid-cols-1 lg:grid-cols-[14rem_1fr_14rem] gap-4 items-start">
                    <div className="sticky top-4 space-y-3">
                        <JobSummaryCards
                            stats={stream.stats}
                            sessionRanges={stream.sessionRanges}
                            extraCards={extraSummaryCards}
                        />
                        {workersCard}
                        {hasSettings && (
                            <DispatcherSettingsCard
                                settings={stream.settings}
                                settingConfigs={settingConfigs}
                                onSettingChange={stream.updateSetting}
                            />
                        )}
                    </div>

                    <div className="space-y-4 min-w-0">
                        {errorAlert}
                        {jobHistoryTable}
                    </div>

                    <div className="sticky top-4">
                        {jobTypeCards(true)}
                    </div>
                </div>
                {jobDetailModal}
                {runJobModal}
            </>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {errorAlert}
                <JobSummaryCards
                    stats={stream.stats}
                    sessionRanges={stream.sessionRanges}
                    extraCards={extraSummaryCards}
                />
                {workersCard}
                {jobTypeCards(false)}
                {jobHistoryTable}
            </div>
            {jobDetailModal}
            {runJobModal}
        </>
    );
};
